import {
  BRAND_CONFIGS,
  detectBrandFromHostname,
  type SiteBrand,
  swapApex,
} from '~/config/branding';
import { withClerkSatelliteSync } from './returnTo';
import { paths } from './routes';

/** The other brand of each brand: the target of every "switch to" link. */
export const OTHER_BRAND: Record<SiteBrand, SiteBrand> = { mcv: 'hlx', hlx: 'mcv' };

/**
 * [MCV-1513] Builds a session-safe cross-brand URL. This is the shared core
 * that both `postAuthRedirect.ts`'s `toExternalUrl` (post-login destinations)
 * and `buildBrandSwitchUrl` below (the header/footer "switch brand"
 * affordance) rely on, so there is exactly one implementation of these host
 * rules:
 *   1. `swapApex` to preserve the env prefix (dev./staging./prod.) across the
 *      brand swap.
 *   2. The bare-apex fallback: a target host of `medcredvault.com` becomes
 *      `prod.medcredvault.com`, because the bare MCV apex doesn't serve the
 *      app (same fallback as the satellite bootstrap in main.tsx).
 *   3. Wraps HLX targets in `withClerkSatelliteSync()` — HLX is a Clerk
 *      satellite, so a plain cross-origin link lands a signed-in user back on
 *      HLX signed out; the sync param is what lets the session carry over.
 *
 * Deliberately does NOT route through `/auth/complete` the way
 * `postAuthRedirect.ts` does on top of this — that layer resolves a signed-in
 * user's role to decide their final destination, which only makes sense right
 * after login. A generic brand switch has no role to resolve, and bouncing a
 * signed-out visitor through `/auth/complete` would force them into sign-in
 * just to look at the other brand's site. `useSatelliteRedirect` only runs on
 * the four auth pages, so landing directly on `targetPath` is safe for
 * anonymous visitors too.
 */
// Full ORIGINS (scheme + host + port), unlike BRAND_CONFIGS[*].domain which is
// a bare apex. Vite fills these from VITE_VAULT_DOMAIN / VITE_HLX_DOMAIN,
// defaulting to http://localhost:3002 / http://localhost:3001 locally and the
// production apexes otherwise (see vite.config.ts).
//
// The apex fallback is not dead code: these are `define` substitutions, so any
// config that omits them (vitest.browser.config.ts, for one) leaves them
// undefined at runtime. Without the fallback that undefined reaches
// `new URL(path, base)`, which THROWS and takes the whole switcher render down
// with it — a broken link is bad, an exception in a shared header is worse.
const configuredOrigin = (value: string | undefined, apex: string): string => {
  if (!value) return `https://${apex}`;
  try {
    return new URL(value).origin;
  } catch {
    return `https://${apex}`;
  }
};

const CONFIGURED_ORIGIN: Record<SiteBrand, string> = {
  mcv: configuredOrigin(import.meta.env.VITE_VAULT_DOMAIN, `prod.${BRAND_CONFIGS.mcv.domain}`),
  hlx: configuredOrigin(import.meta.env.VITE_HLX_DOMAIN, BRAND_CONFIGS.hlx.domain),
};

/**
 * Resolves the origin to send the visitor to.
 *
 * The apex swap only works when the CURRENT host is recognisably one brand's
 * apex or a subdomain of it. On any other host it cannot: `detectBrandFromHostname`
 * returns null, `swapApex` is a no-op, and we would build `https://<current
 * host>/...` — i.e. never leave the brand we are on. That is the case for local
 * dev (`localhost:3002` for MCV / `localhost:3001` for HLX) and for preview
 * hosts, and passing only a bare hostname also drops the scheme and port, so
 * `https://localhost/` was doubly wrong. Fall back to the configured origin,
 * which carries all three parts. [MCV-1513]
 */
const resolveTargetOrigin = (hostname: string, targetBrand: SiteBrand): string => {
  const currentBrand = detectBrandFromHostname(hostname);
  if (!currentBrand) return CONFIGURED_ORIGIN[targetBrand];
  if (currentBrand === targetBrand) return `https://${hostname}`;

  let targetHost = swapApex(
    hostname,
    BRAND_CONFIGS[currentBrand].domain,
    BRAND_CONFIGS[targetBrand].domain
  );
  if (targetHost === BRAND_CONFIGS.mcv.domain) {
    targetHost = `prod.${BRAND_CONFIGS.mcv.domain}`;
  }
  return `https://${targetHost}`;
};

type CrossHostOptions = {
  /**
   * Which Clerk sync marker an HLX target carries.
   *
   * - `'sync'` (default) asks the satellite to sync, which is right for links a
   *   SIGNED-IN visitor follows.
   * - `'none'` stamps nothing. [MCV-1591] Required for an AUTH ENTRY link --
   *   see `buildBrandAuthEntryUrl`.
   * - `'skip'` tells the satellite there is nothing to sync. [MCV-1719] For a
   *   signed-out visitor -- see `buildBrandSwitchUrl`'s `sessionAware`.
   */
  clerkSync?: 'sync' | 'none' | 'skip';
};

export const buildCrossHostUrl = (
  hostname: string,
  targetBrand: SiteBrand,
  targetPath: string,
  { clerkSync = 'sync' }: CrossHostOptions = {}
): string => {
  const targetOrigin = resolveTargetOrigin(hostname, targetBrand);

  // `new URL(path, base)` does NOT keep the base when `path` supplies its own
  // origin: an absolute URL, a protocol-relative `//host`, or a backslash form
  // (`\\host`, `/\host`, which the URL parser folds to `//host`) all silently
  // replace it. Since the result is handed to `window.location.assign`, an
  // unvalidated path here would be an open redirect off the brand's origin.
  //
  // No current caller can reach that -- `buildBrandSwitchUrl` passes an
  // in-app constant, and `postAuthRedirect` launders its `returnTo` through
  // `normalizeReturnTo` first -- but this is an exported primitive that will
  // attract callers, so it enforces the origin itself rather than trusting
  // every future one to. Fails closed: an off-origin path is dropped and the
  // trusted origin's root is used instead.
  let targetUrl: URL;
  try {
    targetUrl = new URL(targetPath, targetOrigin);
  } catch {
    targetUrl = new URL('/', targetOrigin);
  }
  if (targetUrl.origin !== targetOrigin) {
    if (import.meta.env.DEV) {
      console.error(
        '[buildCrossHostUrl] path tried to change origin; ignoring it and using the brand root.',
        { targetPath, expected: targetOrigin, got: targetUrl.origin }
      );
    }
    targetUrl = new URL('/', targetOrigin);
  }

  if (targetBrand !== 'hlx' || clerkSync === 'none') return targetUrl.toString();
  return withClerkSatelliteSync(targetUrl.toString(), { skip: clerkSync === 'skip' });
};

/**
 * [MCV-1591] Cross-brand link to an AUTH ENTRY page (sign-in / sign-up).
 *
 * Never stamps `__clerk_synced`. That param is a RETURN marker: the receiving
 * host's `useSatelliteRedirect` treats its mere presence as "Clerk is handing
 * the browser back, let clerk-js mount", and so declines to bounce to the
 * primary. Putting it on an OUTBOUND link therefore disables the very hand-off
 * the destination needs, and Clerk's `<SignUp>` renders on the satellite --
 * which Clerk forbids.
 *
 * There is also nothing for it to carry here: these links are rendered inside
 * `<SignedOut>`, so there is no session to sync. Leaving the URL bare lets the
 * destination's own hook build the Clerk-legal primary URL, which is existing,
 * spec-covered plumbing.
 */
export const buildBrandAuthEntryUrl = (targetBrand: SiteBrand, path: string): string =>
  buildCrossHostUrl(window.location.hostname, targetBrand, path, { clerkSync: 'none' });

// The brand's PUBLIC landing page, deliberately not its dashboard.
//
// Defaulting to a dashboard made the switcher a no-op for every real visitor,
// because each brand's guards bounce a mismatched role straight back to where
// it came from:
//   - a signed-in MCV provider sent to /hlx/dashboard is bounced by
//     ProtectedHLXRoute, since that path is absent from providerAllowedRoutes;
//   - a signed-in HLX employer sent to /vault/dashboard is bounced back to
//     /hlx/dashboard by ProtectedVaultRoute's EmployerVaultRedirect;
//   - a signed-out visitor is sent to sign-in by both.
// So all three flows ended up back on the brand they started on, or at a login
// form. An earlier comment here claimed those guards made the mismatch "safe";
// they resolve it by UNDOING the switch, which is precisely the bug.
//
// `/` is public on both brands and role-agnostic, so it lands every visitor on
// the other brand without a bounce — which is what the ticket asks for: see
// that the two companies are related, and move between them. Callers that know
// a role-appropriate destination can still pass one explicitly. [MCV-1513]
const DEFAULT_SWITCH_PATH: Record<SiteBrand, string> = {
  mcv: paths.landing,
  hlx: paths.landing,
};

/**
 * [MCV-1719] Whether this browser holds a Clerk session for THIS environment.
 *
 * Read from Clerk's own `__client_uat` cookie rather than a hook, because the
 * public header renders on Clerk-less marketing pages where no provider is
 * mounted. Clerk keeps it at `0` while signed out and at the sign-in
 * timestamp otherwise.
 *
 * Clerk writes both an instance-suffixed `__client_uat_<suffix>` and a bare
 * `__client_uat` on the registrable domain, which dev., staging. and prod.
 * share, so the bare one can belong to another environment. When any
 * suffixed cookie exists only those decide; the bare one is the fallback.
 *
 * Nothing refreshes the cookie on a Clerk-less page, so a session revoked
 * server-side can still read as signed in. That costs the old sync hop,
 * which is the behaviour before this hint existed.
 */
export const hasClerkSessionHint = (): boolean => {
  let cookies: [string, string][];
  try {
    cookies = document.cookie.split(';').map((cookie) => {
      const [name, value = ''] = cookie.trim().split('=');
      return [name, value];
    });
  } catch {
    return false;
  }
  const suffixed = cookies.filter(([name]) => name.startsWith('__client_uat_'));
  const candidates = suffixed.length
    ? suffixed
    : cookies.filter(([name]) => name === '__client_uat');
  return candidates.some(([, value]) => Number(value) > 0);
};

type BrandSwitchOptions = {
  /**
   * [MCV-1719] For surfaces a SIGNED-OUT visitor can reach (the public
   * header, which also heads the sign-in page). The sync request makes the
   * HLX satellite hop through the primary, and on development Clerk
   * instances that hop runs through `/sign-in`. Safari was left sitting
   * there, so "Switch to HealthcareLX" flashed HLX and landed back on the
   * MedCredVault sign-in card. With this set, a visitor without a session is
   * sent with the no-sync marker instead; a signed-in one keeps the sync.
   */
  sessionAware?: boolean;
};

/**
 * Reusable "switch to the other brand" link builder for the header logo
 * dropdown and the footer switch link (MCV-1513).
 *
 * Defaults to the target brand's PUBLIC LANDING ROOT (`/`), deliberately not
 * its dashboard — see `DEFAULT_SWITCH_PATH` above for why a dashboard target
 * bounced every signed-in role straight back to the brand it came from.
 *
 * Pass `path` to send the visitor somewhere more specific, but pick one the
 * visitor is actually allowed to reach: a guarded route the caller's role
 * cannot access reintroduces exactly that bounce. Safe to call unconditionally
 * regardless of sign-in state (see the `buildCrossHostUrl` doc comment above).
 */
export const buildBrandSwitchUrl = (
  targetBrand: SiteBrand,
  path?: string,
  { sessionAware = false }: BrandSwitchOptions = {}
): string =>
  buildCrossHostUrl(
    window.location.hostname,
    targetBrand,
    path ?? DEFAULT_SWITCH_PATH[targetBrand],
    {
      clerkSync: sessionAware && !hasClerkSessionHint() ? 'skip' : 'sync',
    }
  );
